<!--
  Hybrid field list for Passport Builder Fields modal: inherited common fields (read-only; mandatory editable) + editable own fields.
  Card grid (see PassportBuilderInventoryFieldCard); replaces v-table + Actions column.
-->
<script setup lang="ts">
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import { InventTypeFieldSetting, ListFieldList, TypeFieldList } from '@/entities/Inventory/TypeField'
import { ObjectSubTypesSelect } from '@/entities/Inventory/ObjectSubTypes'
import type {
  PassportBuilderDictionaryDto,
  PassportBuilderFieldTypeDto,
  PassportBuilderObjectTypeFieldDraft,
} from '../model/objectTypeFields'
import { useObjectTypesFieldsGroupsLayout } from '@/features/Inventory/ObjectTypes/model/useObjectTypesFieldsGroupsLayout'
import { fieldTypeTranslationKey } from '@/features/Inventory/ObjectTypePassportGrid/model/passportFieldPalette'
import { useConfirm, VIcons, VSelectSearch } from '@/shared/ui'
import PassportBuilderInventoryFieldCard from './PassportBuilderInventoryFieldCard.vue'
import PassportBuilderFieldFlag from './PassportBuilderFieldFlag.vue'
import PassportBuilderFieldTypeSettingsModal from './PassportBuilderFieldTypeSettingsModal.vue'
import { useHelpers, useTranslate } from '@/shared/lib'
import { sortBy } from 'lodash'
import { computed, ref, watch } from 'vue'

type PassportFieldsModalMode = 'exclude_list_types' | 'list_types_only'

const props = withDefaults(
  defineProps<{
    parentFields: ObjectInventoryField[]
    fieldGroups: ObjectTypeGroup[]
    primitiveFieldTypes?: PassportBuilderFieldTypeDto[]
    catalogDictionaries?: PassportBuilderDictionaryDto[]
    isObjectTypeFieldsLoading?: boolean
    isObjectTypeFieldsSaving?: boolean
    /** Own-fields modal: hide `list_types`; references/Перечни: only `list_types`. */
    mode?: PassportFieldsModalMode
  }>(),
  { mode: 'exclude_list_types' },
)

/** Must match parent `v-model:fields` (not default `modelValue`). */
const fields = defineModel<ObjectInventoryField[]>('fields', { required: true })
const objectTypeFields = defineModel<PassportBuilderObjectTypeFieldDraft[]>('objectTypeFields', {
  default: () => [],
})

const emit = defineEmits<{
  addObjectTypeField: []
  deleteObjectTypeField: [localId: string]
}>()

/** v-model can be undefined for one tick on open — composables and computeds assume an array. */
watch(
  () => fields.value,
  (v) => {
    if (v == null || !Array.isArray(v)) fields.value = []
  },
  { immediate: true },
)

const { show } = useConfirm()
const { trs } = useTranslate()
const { generateKey } = useHelpers()

const { groupsView } = useObjectTypesFieldsGroupsLayout(() => props.fieldGroups, fields, trs)

const parentFieldIds = computed(() => (props.parentFields ?? []).map((f) => f.id))

function isInherited(id: string | number) {
  return parentFieldIds.value.some((pid) => String(pid) === String(id))
}

function matchesMode(field: ObjectInventoryField) {
  const isLt = field.type === ListFieldList.LIST_TYPES
  if (props.mode === 'list_types_only') return isLt
  return !isLt
}

const commonRows = computed(() =>
  (fields.value ?? []).filter((f) => isInherited(f.id) && matchesMode(f)),
)
const ownRows = computed(() =>
  (fields.value ?? []).filter((f) => !isInherited(f.id) && matchesMode(f)),
)

const isReferencesMode = computed(() => props.mode === 'list_types_only')
const isObjectTypeFieldsMode = computed(
  () => props.mode === 'exclude_list_types' || props.mode === 'list_types_only',
)
const isCatalogFieldsMode = computed(() => props.mode === 'list_types_only')

/**
 * Возвращает локализованное имя типа поля с fallback на код из API.
 */
function fieldTypeTitle(item: PassportBuilderFieldTypeDto) {
  const key = fieldTypeTranslationKey(item.name)
  if (!key) return item.name
  const title = trs(key)
  return title === key ? item.name : title
}

const primitiveTypeItems = computed(() =>
  (props.primitiveFieldTypes ?? []).map((item) => ({
    title: fieldTypeTitle(item),
    value: item.id,
  })),
)
const catalogDictionaryItems = computed(() => props.catalogDictionaries ?? [])

const ownSectionTitle = computed(() =>
  isReferencesMode.value ? 'Собственные справочники' : trs('itf.passport_builder_own_fields'),
)

const commonSectionTitle = computed(() =>
  isReferencesMode.value ? 'Общие справочники' : trs('itf.passport_builder_common_fields'),
)

const addButtonTitle = computed(() =>
  isReferencesMode.value ? 'Добавить справочник' : trs('itf.add_field'),
)

const visibleOwnRowsCount = computed(() =>
  isObjectTypeFieldsMode.value ? objectTypeFields.value.length : ownRows.value.length,
)

const defaultGroup = computed<ObjectTypeGroup | null>(() => {
  const fgs = props.fieldGroups ?? []
  const groups = sortBy(
    fgs.filter((g) => g.type === 'GROUP'),
    'sort',
  )
  return groups[0] ?? fgs[0] ?? null
})

/**
 * Group id for new `list_types` rows. Prefer LIST tabs (перечни); if none exist, use first GROUP
 * so «Добавить поле» still works (many catalogs only have GROUP tabs).
 */
const groupForNewListTypesField = computed<ObjectTypeGroup | null>(() => {
  const fgs = props.fieldGroups ?? []
  if (!fgs.length) return null
  const listGroups = sortBy(
    fgs.filter((g) => g.type === 'LIST'),
    'sort',
  )
  if (listGroups[0]?.id != null) return listGroups[0]
  const groupGroups = sortBy(
    fgs.filter((g) => g.type === 'GROUP'),
    'sort',
  )
  if (groupGroups[0]?.id != null) return groupGroups[0]
  return sortBy(fgs, 'sort')[0] ?? null
})

function groupTypeForField(field: ObjectInventoryField): string {
  const g = (props.fieldGroups ?? []).find((x) => x.id === field.group)
  return g?.type ?? 'GROUP'
}

function filedType(groupType: string) {
  const list: { type?: string; title: string; value?: string }[] = []
  TypeFieldList.forEach((item) => {
    if (props.mode !== 'list_types_only') {
      if (groupType === 'LIST' && item.name !== 'Дополнительные') return
    }
    const children = item.children.filter((child) => {
      if (props.mode === 'exclude_list_types' && child.key === ListFieldList.LIST_TYPES) {
        return false
      }
      if (props.mode === 'list_types_only' && child.key !== ListFieldList.LIST_TYPES) {
        return false
      }
      return true
    })
    if (!children.length) return
    list.push({ type: 'subheader', title: item.name })
    children.forEach((child) => {
      list.push({ title: child.name, value: child.key })
    })
  })
  return list
}

function addField() {
  if (isObjectTypeFieldsMode.value) {
    emit('addObjectTypeField')
    return
  }

  if (props.mode === 'list_types_only') {
    const g = groupForNewListTypesField.value
    if (!g?.id) return
    if (!fields.value) fields.value = []
    fields.value.push({
      id: generateKey(8),
      name: '',
      type: ListFieldList.LIST_TYPES,
      items: [],
      object_type: 0,
      default_value: '',
      multiple: false,
      count_object: false,
      is_filtered: false,
      is_table: false,
      is_system: false,
      is_global_search: false,
      group: g.id,
    })
    return
  }

  const g = defaultGroup.value
  if (!g?.id) return
  if (!fields.value) fields.value = []
  fields.value.push({
    id: generateKey(8),
    name: '',
    type: ['LIST'].includes(g.type) ? 'computed' : 'string',
    items: [],
    object_type: 0,
    default_value: '',
    multiple: false,
    count_object: false,
    is_filtered: false,
    is_table: false,
    is_system: false,
    is_global_search: false,
    group: g.id,
  })
}

function deleteField(id: string) {
  show({
    title: trs('itf.delete_field'),
    confirmFunc: () => {
      if (!fields.value) fields.value = []
      const idx = fields.value.findIndex((item) => item.id === id)
      if (idx !== -1) fields.value.splice(idx, 1)
    },
  })
}

/**
 * Запрашивает подтверждение удаления поля из нового draft.
 */
function deleteObjectTypeField(localId: string) {
  show({
    title: trs('itf.delete_field'),
    confirmFunc: () => emit('deleteObjectTypeField', localId),
  })
}

/**
 * Переключает поле каталога для фильтра карты и снимает отметку с остальных полей.
 */
function updateCatalogFilter(localId: string, value: boolean | null) {
  if (!value) {
    const field = objectTypeFields.value.find((item) => item.local_id === localId)
    if (field) field.use_in_filter = false
    return
  }

  objectTypeFields.value.forEach((item) => {
    item.use_in_filter = item.local_id === localId
  })
}

const openSettings = ref(false)
const editField = ref<ObjectInventoryField | null>(null)

function editSettingsHandler(field: ObjectInventoryField) {
  editField.value = field
  openSettings.value = true
}

/** Модалка настроек типа для собственных полей (numeric field_type + validation_props). */
const typeSettingsOpen = ref(false)
const typeSettingsLocalId = ref<string | null>(null)

const typeSettingsDraft = computed(() =>
  typeSettingsLocalId.value
    ? (objectTypeFields.value.find((item) => item.local_id === typeSettingsLocalId.value) ?? null)
    : null,
)

function openTypeSettings(item: PassportBuilderObjectTypeFieldDraft) {
  typeSettingsLocalId.value = item.local_id
  typeSettingsOpen.value = true
}

/** Drag-reorder: only non-inherited rows; splicing in the full `fields` list. */
const transferId = ref<string | null>(null)
const transferPos = ref<null | 'bottom' | 'top'>(null)

function clearDropClasses(el: HTMLElement) {
  el.classList.remove(
    'invent-object-types-fields__transfer_top',
    'invent-object-types-fields__transfer_bottom',
  )
}

function dragStartHandler(id: string | number) {
  if (isInherited(id)) return
  transferId.value = String(id)
}

function dragOverHandler(event: DragEvent) {
  if (!event.currentTarget) return
  const element = event.currentTarget as HTMLElement
  const coords = element.getBoundingClientRect()
  const mousePosition = event.clientY
  clearDropClasses(element)
  if (coords.top + coords.height / 2 > mousePosition) {
    element.classList.add('invent-object-types-fields__transfer_top')
    transferPos.value = 'top'
  } else {
    element.classList.add('invent-object-types-fields__transfer_bottom')
    transferPos.value = 'bottom'
  }
}

function dropLeaveHandler(event: DragEvent) {
  if (!event.currentTarget) return
  clearDropClasses(event.currentTarget as HTMLElement)
}

function dropHandler(event: DragEvent, targetId: string) {
  if (transferId.value === null || transferPos.value === null) return
  if (isInherited(transferId.value) || isInherited(targetId)) {
    transferId.value = null
    transferPos.value = null
    return
  }
  if (!fields.value) fields.value = []
  const model = fields.value
  const elementIndex = model.findIndex((item) => item.id === transferId.value)
  const targetElementIndex = model.findIndex((item) => item.id === targetId)
  if (elementIndex !== -1 && targetElementIndex !== -1) {
    let finalTargetIndex = targetElementIndex + (transferPos.value === 'bottom' ? 1 : 0)
    if (elementIndex < finalTargetIndex) {
      finalTargetIndex--
    }
    const [removed] = model.splice(elementIndex, 1)
    model.splice(finalTargetIndex, 0, removed)
  }
  transferId.value = null
  transferPos.value = null
  clearDropClasses(event.currentTarget as HTMLElement)
}
</script>

<template>
  <div class="pb-fields-modal">
    <div class="pb-fields-toolbar">
      <span class="pb-fields-toolbar__count text-body-2 text-medium-emphasis" aria-live="polite">
        {{ ownSectionTitle }}:
        <strong class="font-weight-bold">{{ visibleOwnRowsCount }}</strong>
      </span>
      <VBtn
        color="primary"
        variant="flat"
        size="default"
        rounded="lg"
        class="pb-fields-toolbar__add"
        @click.prevent="addField"
      >
        <span class="pb-fields-toolbar__add-icon" aria-hidden="true">
          <VIcons type="add" />
        </span>
        {{ addButtonTitle }}
      </VBtn>
    </div>

    <template v-if="isObjectTypeFieldsMode">
      <VAlert
        v-if="props.isObjectTypeFieldsLoading"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        {{ $t('itf.loading_fields') }}
      </VAlert>

      <div v-if="objectTypeFields.length" class="pb-object-fields">
        <article
          v-for="item in objectTypeFields"
          :key="item.local_id"
          class="pb-object-field"
        >
          <div class="pb-object-field__flags" role="group" :aria-label="$t('itf.field_flags')">
            <div class="pb-object-field__id">
              <template v-if="item.id != null">
                <VIcons type="numbers" />
                {{ item.id }}
              </template>

              <template v-else>
                <VChip size="x-small" variant="tonal" label>
                  {{ $t('itf.field_new') }}
                </VChip>
              </template>
            </div>

            <div class="pb-object-field__flags-center">
              <PassportBuilderFieldFlag
                v-if="isCatalogFieldsMode"
                :model-value="item.use_in_filter"
                icon="filter_alt"
                :tooltip="$t('itf.show_in_filter')"
                :disabled="props.isObjectTypeFieldsSaving"
                @update:model-value="updateCatalogFilter(item.local_id, $event ?? null)"
              />

              <PassportBuilderFieldFlag
                v-model="item.use_in_search"
                icon="search"
                :tooltip="$t('itf.is_global_search')"
                :disabled="props.isObjectTypeFieldsSaving"
              />
            </div>

            <div class="pb-object-field__flags-right">
              <PassportBuilderFieldFlag
                v-model="item.is_system"
                icon="visibility_off"
                :tooltip="$t('itf.is_system_field')"
                :disabled="props.isObjectTypeFieldsSaving"
              />
            </div>
          </div>

          <header class="pb-object-field__header">
            <PassportBuilderFieldFlag
              v-model="item.is_required"
              icon="asterisk"
              :tooltip="$t('itf.required')"
              :disabled="props.isObjectTypeFieldsSaving"
            />

            <VTextField
              v-model="item.name"
              class="pb-object-field__name"
              density="compact"
              variant="outlined"
              :label="$t('itf.name_label')"
              :placeholder="$t('itf.new_field_name_placeholder')"
              hide-details
              :disabled="props.isObjectTypeFieldsSaving"
            />
            <VBtn
              v-if="!isCatalogFieldsMode"
              class="pb-object-field__square-btn"
              color="default"
              variant="tonal"
              :aria-label="$t('itf.settings')"
              :title="$t('itf.settings')"
              :disabled="props.isObjectTypeFieldsSaving"
              @click="openTypeSettings(item)"
            >
              <VIcons type="settings" />
            </VBtn>
            <VBtn
              class="pb-object-field__square-btn"
              color="error"
              variant="tonal"
              :aria-label="$t('itf.delete')"
              :title="$t('itf.delete')"
              :disabled="props.isObjectTypeFieldsSaving"
              @click="deleteObjectTypeField(item.local_id)"
            >
              <VIcons type="delete" />
            </VBtn>
          </header>

          <div
            class="pb-object-field__main"
            :class="{ 'pb-object-field__main_catalog': isCatalogFieldsMode }"
          >
            <template v-if="isCatalogFieldsMode">
              <VSelectSearch
                v-model="item.catalog_id"
                :items="catalogDictionaryItems"
                class="pb-object-field__dictionary"
                :label="$t('itf.select_directory')"
                item-value="id"
                item-title="name"
                hide-details
                :min-search-length="0"
                :disabled="props.isObjectTypeFieldsSaving"
              />
              <ObjectSubTypesSelect
                :model-value="item.default_value || null"
                :type-id="Number(item.catalog_id) || 0"
                class="pb-object-field__default"
                :label="$t('itf.default_value')"
                hide-details
                @update:model-value="item.default_value = $event != null ? String($event) : ''"
              />
            </template>
            <template v-else>
              <VSelect
                v-model="item.field_type"
                :items="primitiveTypeItems"
                class="pb-object-field__type"
                density="compact"
                variant="outlined"
                :label="$t('itf.type_field')"
                hide-details
                :disabled="props.isObjectTypeFieldsSaving"
              />
              <VTextField
                v-model="item.default_value"
                class="pb-object-field__default"
                density="compact"
                variant="outlined"
                :label="$t('itf.default_value')"
                hide-details
                :disabled="props.isObjectTypeFieldsSaving"
              />
            </template>
          </div>
        </article>
      </div>

      <VAlert
        v-else-if="!props.isObjectTypeFieldsLoading"
        type="info"
        variant="tonal"
        density="compact"
      >
        {{ $t('itf.fields_empty') }}
      </VAlert>
    </template>

    <VExpansionPanels v-else multiple variant="accordion" class="pb-fields-acc" :model-value="['own']">
      <VExpansionPanel v-if="commonRows.length" value="common">
        <VExpansionPanelTitle>
          <span class="pb-fields-acc__title">
            {{ commonSectionTitle }}
          </span>
          <span class="pb-fields-acc__meta text-body-2 text-medium-emphasis">
            <strong class="font-weight-bold">{{ commonRows.length }}</strong>
          </span>
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div class="pb-fields-card-grid">
            <article v-for="item in commonRows" :key="item.id" class="pb-inv-field-wrap">
              <PassportBuilderInventoryFieldCard
                :item="item"
                :type-items="filedType(groupTypeForField(item))"
                readonly
                :show-drag-grip="false"
              />
            </article>
          </div>
        </VExpansionPanelText>
      </VExpansionPanel>

      <VExpansionPanel value="own">
        <VExpansionPanelTitle>
          <span class="pb-fields-acc__title">
            {{ ownSectionTitle }}
          </span>
          <span class="pb-fields-acc__meta text-body-2 text-medium-emphasis">
            <strong class="font-weight-bold">{{ ownRows.length }}</strong>
          </span>
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div class="pb-fields-card-grid">
            <article
              v-for="item in ownRows"
              :key="item.id"
              class="pb-inv-field-wrap"
              draggable="true"
              @drop="(e) => dropHandler(e, item.id)"
              @dragover.prevent="dragOverHandler"
              @dragleave.prevent="dropLeaveHandler"
              @dragstart="dragStartHandler(item.id)"
            >
              <PassportBuilderInventoryFieldCard
                :item="item"
                :type-items="filedType(groupTypeForField(item))"
                :show-drag-grip="true"
                @edit-settings="editSettingsHandler(item)"
                @delete="deleteField(item.id)"
              />
            </article>
          </div>
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <InventTypeFieldSetting
      v-if="editField"
      :groups="groupsView"
      :hide-mandatory="true"
      v-model="openSettings"
      v-model:field="editField"
    />

    <PassportBuilderFieldTypeSettingsModal
      v-if="typeSettingsDraft"
      v-model="typeSettingsOpen"
      :draft="typeSettingsDraft"
      :field-types="primitiveFieldTypes"
    />
  </div>
</template>

<style scoped lang="scss">
.pb-fields-modal {
  min-width: 0;
}

.pb-fields-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 4px 12px;
}

.pb-fields-toolbar__count {
  flex: 1 1 auto;
  min-width: 0;
}

.pb-fields-toolbar__add {
  font-weight: 600;
  letter-spacing: 0.02em;
  padding-inline: 18px !important;
  min-height: 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.pb-fields-toolbar__add-icon {
  display: inline-flex;
  margin-inline-end: 8px;
  opacity: 0.95;
}

.pb-fields-toolbar__add-icon :deep(.v-icon) {
  font-size: 20px;
}

.pb-fields-acc {
  margin-bottom: 8px;
}

.pb-fields-acc__title {
  font-weight: 600;
}

.pb-fields-acc__meta {
  margin-left: auto;
}

.pb-fields-card-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  align-items: stretch;
}

.pb-object-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.pb-object-field {
  min-width: 0;
  padding: 4px 8px 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
}

.pb-object-field__id {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  line-height: 1;
  color: rgba(0, 0, 0, 0.54);

  .v-icon {
    font-size: 18px;
  }
}

.pb-object-field__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.pb-object-field__name {
  min-width: 0;
  flex: 1 1 auto;
}

.pb-object-field__square-btn {
  box-sizing: border-box;
  flex: 0 0 auto;
  min-width: 40px;
  padding: 8px;
}

.pb-object-field__main {
  display: grid;
  grid-template-columns: minmax(160px, 220px) minmax(0, 1fr);
  gap: 8px;
}

.pb-object-field__main_catalog {
  grid-template-columns: 1fr 1fr;
}

.pb-object-field__flags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 16px;
  margin-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.pb-object-field__flags-center {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.pb-object-field__flags-right {
  display: flex;
  align-items: center;
  margin-left: auto;
}

@media (max-width: 1100px) {
  .pb-fields-card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pb-object-fields {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .pb-fields-card-grid {
    grid-template-columns: 1fr;
  }

  .pb-object-field__main {
    grid-template-columns: 1fr;
  }
}

.pb-inv-field-wrap {
  position: relative;
  min-width: 0;
}

.pb-inv-field-wrap.invent-object-types-fields__transfer_bottom::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  right: 0;
  height: 4px;
  background: rgb(var(--v-theme-primary));
  pointer-events: none;
  border-radius: 2px;
}

.pb-inv-field-wrap.invent-object-types-fields__transfer_top::before {
  content: '';
  position: absolute;
  top: -2px;
  left: 0;
  right: 0;
  height: 4px;
  background: rgb(var(--v-theme-primary));
  pointer-events: none;
  border-radius: 2px;
}
</style>
